import { forwardRef, memo, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import './index.scss'
import Icon from '../Icon'
import useDynamicRef from '../../hooks/useDynamicRef'
import { Circle as CircleType, Point as PointType, Polygon as PolygonType, MapIcon } from './context/types'
import VectorLayer from 'ol/layer/Vector'
import { Feature, Map, Overlay, View } from 'ol'
import { Circle, Geometry, LineString, MultiLineString, Point, Polygon } from 'ol/geom'
import CircleStyle from 'ol/style/Circle'
import { unByKey } from 'ol/Observable'
import { easeOut } from 'ol/easing'
import { Cluster, Vector } from 'ol/source'
import { useMap } from '../../hooks/useMap'
import { CENTER_ICELAND_COORDS, DEFAULT_MAP_ZOOM, PROJECTION_TYPE } from './context/reducer'
import UserContext from '../../contexts/UserContext/context'
import Button from '../Button'
import { ICON } from '../Icon/data'
import { default as olIcon } from 'ol/style/Icon'
import Style from 'ol/style/Style'
import FlotiMapContext from './context/context'
import { setMapCircles, setMapIcons, setMapPoints, setMapPolyshapes, setMapTile } from './context/actions'
import FlotiMapState from './context'
import { Coordinate } from 'ol/coordinate'
import Stroke from 'ol/style/Stroke'
import Fill from 'ol/style/Fill'
import Text from 'ol/style/Text'
import { COLOR } from '../../data/variables'
import VectorSource from 'ol/source/Vector'
import Draw, { DrawEvent } from 'ol/interaction/Draw'
import Infobox from '../Infobox'
import { useTranslation } from 'react-i18next'
import { FlotiMapRef, FlotiMapSettings, FlotiMap as FlotiMapType, MapOptions, MapInfoDisplay } from './type'
import { DateTime } from 'luxon'
import { getVectorContext } from 'ol/render';
import BaseEvent from 'ol/events/Event'
import { useWorkingQueue } from '../../hooks/useWorkingQueue'
import { MAP_TYPE_IDS, MAP_TYPES } from './data'
import google_logo from '../../assets/google.png'
import { useTiles } from './useTiles'
import { useOptions } from './useOptions'
import { useQuery } from 'react-query'
import { getLiveCams, getRoadConditions, getWeather } from '../../services/calls/maps'
import { useSidebar } from '../../hooks/useSidebar'
import { MAP_ITEM_INFO } from './sidebars/MapDataDisplay/data'
import MapDataDisplay from './sidebars/MapDataDisplay'
import { FeatureLike } from 'ol/Feature'
import { EventsKey } from 'ol/events'
import { colorToString } from '../../services/utils/convert'

export enum LAYER_TYPE {
  POLYGONS = 'polygons',
  CIRCLES = 'circles',
  MARKERS = 'markers',
  TRAILS = 'trails',
  DRAWING = 'drawing',
  TILE = 'tile',
  ROAD_CONDITIONS = 'roadConditions',
  ROAD_SIGNS = 'roadSigns',
  WEATHER = 'weather',
  LIVE_CAMS = 'liveCams',
  CLUSTER = 'cluster',
  ICONS = 'icons'
}

enum DRAWING_MAP {
  polygon = 'Polygon',
  circle = 'Circle'
}

const DEFAULT_OPTIONS: MapOptions = {
  showTiles: true,
  showOptions: true,
  showZoom: true,
  markerScale: 50,
  clusterIcons: false,
}

const BLINK_DURATION = 1500
const CLUSTER_DISTANCE = 40
const CLUSTER_MIN_DISTANCE = 20

const FlotiMap = memo(forwardRef<FlotiMapRef, FlotiMapType>(({
  readonly = false, mapSettings, showTrails = false, drawCallback = () => {},
  isBusyCallback = () => {}, defaultTile = MAP_TYPE_IDS.OPEN_STREETMAP,
  options: mapOptions = {}, layerOptions = {}
}, ref) => {
  const map = useRef<Map | null>(null)
  const mapRef = useRef<HTMLDivElement | null>(null)
  const animateRef = useRef<EventsKey | null>(null)

  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const { flotiMap, dispatch } = useContext(FlotiMapContext)
  const { points, icons, polygons, circles, tile: selectedTile, viewOptions } = flotiMap
  const { updateInfobar } = useSidebar()

  const [drawingEnabled, setDrawingEnabled] = useState<boolean>(false)
  const [infoDisplay, setInfoDisplay] = useState<MapInfoDisplay | null>(null)

  const { radiansToDegrees, getPointStyle, degreesToRadians, calculateIconScale } = useMap()
  const { isBusy, addToQueue, removeFromQueue } = useWorkingQueue()
  const tiles = useTiles()
  const { mapTypeSelector, viewOptionsSelector } = useOptions()

  const options = useMemo(() => ({ ...DEFAULT_OPTIONS, ...mapOptions }), [mapOptions])

  const { data: roadConditions } = useQuery(['getRoadConditions'], () => getRoadConditions(), {
    enabled: options.showOptions
  })

  const { data: weather } = useQuery(['getWeather'], () => getWeather(), {
    enabled: mapOptions.showOptions
  })

  const { data: liveCams } = useQuery(['getLiveCams'], () => getLiveCams(), {
    enabled: mapOptions.showOptions
  })

  const getProjection = useCallback(() => map?.current?.getView().getProjection(), [map])

  useEffect(() => {
    isBusyCallback(isBusy)
  }, [isBusy])

  const liveCamLookup = useMemo(() => {
    const lookup = {}
    if (liveCams) {
      liveCams.forEach((cam) => {
        if (!lookup[cam.Maelist_nr]) {
          lookup[cam.Maelist_nr] = {
            lat: cam.Breidd,
            lon: cam.Lengd,
            id: cam.Maelist_nr,
            name: cam.Myndavel,
            description: cam.Skyring,
            road: cam.Vegheiti,
            img: []
          }
        }

        lookup[cam.Maelist_nr].img.push(cam.Slod)
      })
    }
    return lookup
  }, [liveCams])

  const setLayerVisibility = useCallback((type: LAYER_TYPE, visibility: boolean) => {
    map?.current?.getLayers()?.getArray().forEach((layer) => {
      if (layer.get('type') === type) layer.setVisible(visibility)
    })
  }, [map])

  useEffect(() => {
    setLayerVisibility(LAYER_TYPE.ROAD_CONDITIONS, viewOptions?.roadConditions)
  }, [viewOptions?.roadConditions])

  useEffect(() => {
    setLayerVisibility(LAYER_TYPE.WEATHER, viewOptions?.weather)
  }, [viewOptions?.weather])

  useEffect(() => {
    setLayerVisibility(LAYER_TYPE.LIVE_CAMS, viewOptions?.liveCams)
  }, [viewOptions?.liveCams])

  const onItemClick = useCallback((id: number, type: LAYER_TYPE) => {
    let item
    if (type === LAYER_TYPE.ROAD_CONDITIONS || type === LAYER_TYPE.ROAD_SIGNS) {
      const { roads_to_draw } = roadConditions ?? { roads_to_draw: []}
      item = roads_to_draw.find((r) => Number(r?.road_internal_id) === Number(id))
    } else if (type === LAYER_TYPE.WEATHER) {
      item = weather?.find((w) => w.Nr === id)
    } else if (type === LAYER_TYPE.LIVE_CAMS) {
      item = liveCamLookup[id]
    }

    if (item) {
      updateInfobar({
        isOpen: true,
        header: (
          <div className="Sidebar__header-label">
            {MAP_ITEM_INFO[type]?.label}
          </div>
        ),
        content: <MapDataDisplay data={item} type={type} />
      })
    }
  }, [roadConditions, updateInfobar])

  const getLayer = useCallback((type?: LAYER_TYPE) => {
    const layer = map?.current?.getLayers()?.getArray()?.find((layer) => layer?.get('type') === type)
    return layer ?? null
  }, [map])

  const fitInMap = useCallback((layerType: LAYER_TYPE, id?: number | string) => {
    map?.current?.render()
    const layer = getLayer(layerType)
    const view = map?.current?.getView()
    if (id) {
      const features = layer?.get('source').getFeatures()
      const feature = features?.find((f) => f.getId() === id)
      const geometry = feature?.getGeometry()
      if (geometry) view?.fit(geometry, { padding: [300, 300, 300, 300]})
    } else {
      const extent = layer?.get('source').getExtent()
      if (extent) view?.fit(extent, { padding: [50, 50, 50, 50] })
    }
  }, [getLayer, map])

  const removeElem = useCallback((id: string) => {
    const elem = document.getElementById(id)
    if (elem) {
      elem.style.display = 'none'
    }
  }, [])

  const updateMapEvents = useCallback(() => {
    map?.current?.on('singleclick', (e) => {
      let idx = 0
      map.current?.forEachFeatureAtPixel(e.pixel, (f) => {
        if (idx === 0) { // Only take the top feature
          const feature = typeof f.get('type') === 'undefined' ? f.getProperties().features[0] : f
          const type = feature.get('type')
          if (type === LAYER_TYPE.ROAD_CONDITIONS || type === LAYER_TYPE.ROAD_SIGNS) {
            const road_id = feature.get('road_id')
            onItemClick(road_id, type)
          } else if (type === LAYER_TYPE.WEATHER) {
            const weather_id = feature.get('weather_id')
            onItemClick(weather_id, type)
          } else if (type === LAYER_TYPE.LIVE_CAMS) {
            const camera_id = feature.get('cam_id')
            onItemClick(camera_id, type)
          } else if ((type === LAYER_TYPE.ICONS) && feature.get('overlay')) {
            const coordinates = feature?.getGeometry()?.getCoordinates() ?? e.coordinate
            const element = document.getElementById('overlay')
            const isCluster = f.getProperties().features.length > 1
            if (element && !isCluster) {
              const textElem = document.getElementById('overlay_text')
              if (textElem) textElem.innerHTML = feature.get('overlay')
              element.style.display = 'flex'
              const overlay = new Overlay({
                element,
                stopEvent: false,
                positioning: 'bottom-center'
              })
              overlay.setPosition(coordinates)
              map.current?.addOverlay(overlay)
            }
          }
        }
        idx = idx + 1
      })
    })

    const prevZoom = map.current?.getView().getZoom()
    map?.current?.on('moveend', () => {
      const zoom = map.current?.getView().getZoom()
      if (prevZoom !== zoom) {
        map.current?.getLayers().getArray().forEach((layer) => {
          if (layer.get('type') === LAYER_TYPE.MARKERS) {
            layer.get('source').getFeatures().forEach((feature: Feature) => {
              const style = feature.getStyle()
              const image = style.getImage()
              image.setScale(calculateIconScale(zoom))
              style.setImage(image)
              feature.setStyle(style)
            })
          }
        })
      }
    })
  }, [onItemClick, map, getLayer, removeElem])

  const createPoint = useCallback((lat: number, lon: number, isRadian = true) => {
    const projection = getProjection()
    if (projection) {
      const latDegree = isRadian ? radiansToDegrees(lat) : lat
      const lonDegree = isRadian ? radiansToDegrees(lon) : lon
      const point = new Point([lonDegree, latDegree]).transform(PROJECTION_TYPE, projection)
      return point
    }
  }, [radiansToDegrees, getProjection])

  const setMapSettings = useCallback(({ zoom, center }: FlotiMapSettings, isCoords: boolean = false) => {
    const view = map?.current?.getView()
    let coords = center
    if (isCoords && center) {
      const point = createPoint(center[0], center[1])
      coords = point.getFlatCoordinates()
    }

    if (view) {
      if (zoom) view.setZoom(zoom)
      if (center) view.setCenter(coords)
    }
  }, [map, createPoint])

  const createPolygon = useCallback((polyArr: Array<Coordinate>) => {
    const projection = getProjection()
    const polygon = new Polygon([polyArr]).transform(PROJECTION_TYPE, projection)
    return polygon
  }, [getProjection])

  const getMapSettings = useCallback(() => {
    const view = map?.current?.getView()
    const zoom = view?.getZoom()
    const center = view?.getCenter()
    return {
      zoom,
      center
    }
  }, [map])

  const setPoints = useCallback((newPoints: PointType[]) => {
    addToQueue(LAYER_TYPE.MARKERS)
    dispatch(setMapPoints(newPoints))
  }, [dispatch, addToQueue])

  const setIcons = useCallback((newIcons: MapIcon[]) => {
    addToQueue(LAYER_TYPE.ICONS)
    dispatch(setMapIcons(newIcons))
  }, [dispatch])

  const setPolyshapes = useCallback((newPolyshapes: PolygonType[]) => {
    addToQueue(LAYER_TYPE.POLYGONS)
    dispatch(setMapPolyshapes(newPolyshapes))
  }, [dispatch])

  const setCircles = useCallback((newCircles: CircleType[]) => {
    addToQueue(LAYER_TYPE.CIRCLES)
    dispatch(setMapCircles(newCircles))
  }, [dispatch])

  const getMarkerFeatures = useCallback((coords: PointType[] = []) => {
    const zoom = map?.current?.getView().getZoom()
    const markers = coords?.map((p) => {
      const pStyle = getPointStyle(zoom, p.icon, false, {
        heading: p.heading,
        color: p.color ? colorToString(p.color) : COLOR.MARKER_BLUE,
        scale: options.markerScale
      })
      const point = createPoint(p.lat, p.lon)
      const feature = new Feature(point)
      feature.setId(p.id)
      feature.set('type', LAYER_TYPE.MARKERS)
      feature.setStyle(new Style({
        image: new olIcon(pStyle),
        text: new Text({
          text: p.name,
          overflow: true,
          textBaseline: 'top',
          offsetY: 10,
          fill: new Fill({
            color: COLOR.WHITE
          }),
          stroke: new Stroke({
            color: COLOR.BLACK,
            width: 3
          })
        })
      }))
      return feature
    })
    return markers
  }, [map, createPoint, getPointStyle])

  const getIconFeatures = useCallback((coords: MapIcon[] = []) => {
    const zoom = map?.current?.getView().getZoom()
    const mapIcons = coords.map((i, idx) => {
      const pStyle = getPointStyle(zoom, i.icon, false, {
        color: i.color ? colorToString(i.color) : COLOR.MARKER_BLUE,
        scale: 80
      })
      const point = createPoint(i.lat, i.lon)
      const feature = new Feature(point)
      feature.setId(idx)
      feature.set('type', LAYER_TYPE.ICONS)
      if (i.overlay) feature.set('overlay', i.overlay)
      feature.setStyle(new Style({
        image: new olIcon(pStyle),
        text: new Text({
          text: i.text,
          overflow: true,
          textBaseline: 'top',
          offsetY: -7,
          fill: new Fill({
            color: COLOR.WHITE
          })
        })
      }))
      return feature
    })
    return mapIcons
  }, [map, createPoint, getPointStyle])

  const getPolygonFeatures = useCallback((shapes: PolygonType[]) => {
    const polygonArr = shapes?.map((c) => {
      const polygon = createPolygon(c.polyshape)
      const feature = new Feature({
        geometry: polygon,
        label: c.label,
      })
      feature.setStyle(new Style({
        stroke: new Stroke({
          color: c.stroke?.color ?? 'red',
          width: c.stroke?.width ?? 3
        }),
        fill: new Fill({
          color: c.color ?? 'rgba(255, 0, 0, 0.3)'
        }),
        text: new Text({
          text: c.label,
          overflow: true,
          stroke: new Stroke({
            color: COLOR.WHITE,
            width: 2,
          })
        })
      }))
      feature.setId(c.id)
      return feature
    })
    return polygonArr
  }, [createPolygon])

  const getCircleFeatures = useCallback((shapes: CircleType[]) => {
    const circleArr = shapes?.map((s) => {
      const projection = getProjection()
      const circle = new Circle([s.lat, s.lon], s.radius).transform(PROJECTION_TYPE, projection)
      circle.setRadius(s.radius) // Re-set so radius isn't transformed
      const feature = new Feature({
        geometry: circle
      })
      feature.setStyle(new Style({
        fill: new Fill({
          color: s.color ?? 'rgba(216, 31, 40, 0.3)'
        }),
        stroke: new Stroke({
          width: s.stroke?.width ?? 1,
          color: s.stroke?.color ?? 'rgba(2, 91, 156, 1)'
        }),
        text: new Text({
          text: s.label,
          overflow: true,
          stroke: new Stroke({
            color: COLOR.WHITE,
            width: 2,
          })
        })
      }))
      feature.setId(s.id)
      return feature
    })
    return circleArr
  }, [map, getProjection])

  const addMapLayer = useCallback((source: Vector, type: string, visible: boolean = true, vOptions = { zIndex: 0 }) => {
    const vectorLayer = new VectorLayer({
      source,
      visible,
      zIndex: vOptions.zIndex
    })
    if (vOptions.style) {
      vectorLayer.setStyle(vOptions.style)
    }

    const layerOption = layerOptions[type] ?? {}
    if (layerOption.minZoom) vectorLayer.setMinZoom(layerOption.minZoom)
    if (layerOption.maxZoom) vectorLayer.setMaxZoom(layerOption.maxZoom)

    vectorLayer.set('type', type)
    map?.current?.addLayer(vectorLayer)
  }, [map, layerOptions])

  const getTrailFeatures = useCallback((coords: PointType[] = []) => {
    let startPoint
    const trails = coords?.sort((a, b) => a?.date > b?.date ? 1 : -1).reduce((arr, p) => {
      const point = createPoint(p.lat, p.lon)
      if (!startPoint) {
        startPoint = point
        return arr
      }

      const feature = new Feature({
        geometry: new LineString([startPoint.getCoordinates(), point.getCoordinates()])
      })
      feature.setId(p.id)
      feature.setStyle(new Style({
        stroke: new Stroke({
          width: 2,
          color: COLOR.BLUE_70,
        })
      }))
      arr.push(feature)
      startPoint = point
      return arr
    }, [])

    return trails
  }, [createPoint])

  const animateBlink = useCallback((e: BaseEvent, feature: Geometry, startTime: number, numBlinks: number) => {
    const frameState = e.frameState
    const blinkCount = numBlinks - Math.floor((frameState.time - startTime) / BLINK_DURATION)
    const elapsed = (frameState.time - startTime) - (BLINK_DURATION * (numBlinks - blinkCount))

    if (blinkCount === 0) {
      unByKey(animateRef.current)
      animateRef.current = null
      return;
    }

    const vectorContext = getVectorContext(e)
    const elapsedRatio = elapsed / BLINK_DURATION
    // Radius will be 5 at start and 30 at end
    const radius = easeOut(elapsedRatio) * 25 + 5
    const opacity = easeOut(1 - elapsedRatio)

    const style = new Style({
      image: new CircleStyle({
        radius: radius,
        stroke: new Stroke({
          color: 'rgba(255, 0, 0, ' + opacity + ')',
          width: 0.25 + opacity,
        }),
      }),
    })

    vectorContext.setStyle(style)
    vectorContext.drawGeometry(feature)
    map?.current?.render() // Continue postrender animation untin unByKey stops the render circle
  }, [map, animateRef.current])

  const makeBlink = useCallback(async (id: number, type: LAYER_TYPE, numBlinks: number = 3) => {
    // Make sure there's only one vehicle blinking at a time
    if (animateRef.current) {
      unByKey(animateRef.current)
      animateRef.current = null
    }

    const layer = getLayer(type)
    if (id) {
      const features = layer?.get('source').getFeatures()
      const feature = features?.find((f) => f.getId() === id)
      const flashGeom = feature.getGeometry().clone()
      const tileLayer = getLayer()
      let now = DateTime.now().toMillis()
      animateRef.current = tileLayer?.on('postrender', (e) => animateBlink(e, flashGeom, now, numBlinks))
    }
  }, [animateBlink, getLayer, animateRef.current])

  const clearLayers = useCallback(async (type?: string) => {
    map?.current?.getLayers().forEach((layer) => {
      const layerType = layer?.get('type')
      if (type) {
        if (layerType === type) { 
          map.current?.removeLayer(layer)
        }
      } else if (layer && layerType !== 'tile') {
        map.current?.removeLayer(layer)
      }
    })
  }, [map])

  const createTrailVector = useCallback(async () => {
    const type = LAYER_TYPE.TRAILS
    await clearLayers(type)
    const coords = points?.length ? [...points] : []
    const source = new Vector({
      features: getTrailFeatures(coords)
    })

    addMapLayer(source, type)
    removeFromQueue(LAYER_TYPE.TRAILS)
  }, [getTrailFeatures, points, clearLayers, removeFromQueue])

  const clusterStyleFunc = useCallback((feature: FeatureLike) => {
    const features = feature.get('features')
    const size = features.length
    let style = features[0].getStyle()
    if (size > 1) {
      style = new Style({
        image: new CircleStyle({
          radius: 15,
          stroke: new Stroke({
            color: COLOR.WHITE,
            width: 2,
          }),
          fill: new Fill({
            color: COLOR.ORANGE_40,
          }),
        }),
        text: new Text({
          text: size.toString(),
          fill: new Fill({
            color: COLOR.WHITE,
          }),
        }),
      });
    }
    return style
  }, [])

  const createMarkerVector = useCallback(async () => {
    const type = LAYER_TYPE.MARKERS
    await clearLayers(type)
    const coords = points?.length ? [...points] : []
    const source = new Vector({
      features: getMarkerFeatures(coords),
    })

    const clusterSource = new Cluster({
      distance: CLUSTER_DISTANCE,
      minDistance: CLUSTER_MIN_DISTANCE,
      type: LAYER_TYPE.CLUSTER,
      source
    })

    addMapLayer(clusterSource, LAYER_TYPE.CLUSTER, viewOptions?.clusters, { style: clusterStyleFunc })
    addMapLayer(source, LAYER_TYPE.MARKERS, !viewOptions?.clusters, { zIndex: 10 })
    removeFromQueue(LAYER_TYPE.MARKERS)
  }, [
    points, addMapLayer, getMarkerFeatures, clearLayers, removeFromQueue, clusterStyleFunc,
    viewOptions
  ])

  const createIconVector = useCallback(async () => {
    const type = LAYER_TYPE.ICONS
    await clearLayers(type)
    const coords = icons?.length ? [...icons] : []
    const source = new Vector({
      features: getIconFeatures(coords),
    })

    const clusterSource = new Cluster({
      distance: CLUSTER_DISTANCE,
      minDistance: CLUSTER_MIN_DISTANCE,
      type: LAYER_TYPE.CLUSTER,
      source
    })

    addMapLayer(clusterSource, LAYER_TYPE.CLUSTER, options.clusterIcons, { style: clusterStyleFunc, zIndex: 12 })
    addMapLayer(source, LAYER_TYPE.ICONS, !options.clusterIcons, { zIndex: 11 })
    removeFromQueue(LAYER_TYPE.ICONS)
  }, [
    icons, getIconFeatures, addMapLayer, removeFromQueue, clearLayers, options, clusterStyleFunc,
  ])

  const createPolygonVector = useCallback(async () => {
    const type = LAYER_TYPE.POLYGONS
    await clearLayers(type)
    const source = new Vector({
      features: getPolygonFeatures(polygons)
    })

    addMapLayer(source, type)
    removeFromQueue(LAYER_TYPE.POLYGONS)
  }, [polygons, addMapLayer, getPolygonFeatures, clearLayers, removeFromQueue])

  const createCircleVector = useCallback(async () => {
    const type = LAYER_TYPE.CIRCLES
    await clearLayers(type)
    const source = new Vector({
      features: getCircleFeatures(circles)
    })

    addMapLayer(source, type)
    removeFromQueue(LAYER_TYPE.CIRCLES)
  }, [circles, addMapLayer, getCircleFeatures, clearLayers, removeFromQueue])

  const onDrawEnd = useCallback((e: DrawEvent, shape: 'circle' | 'polygon') => {
    const geometry = e.feature.getGeometry()
    const projection = getProjection()
    if (shape === 'polygon') {
      const rawCoordinates = geometry.getCoordinates()?.[0]
      const coordinates = rawCoordinates.reduce((arr, coord: Coordinate) => {
        const lat_rad = coord[0]
        const lon_rad = coord[1]
        const point = new Point([lat_rad, lon_rad]).transform(projection, PROJECTION_TYPE).getFlatCoordinates()
        arr.push([point[0], point[1]])
        return arr
      }, [])
      drawCallback({
        coordinates,
        type: 'polygon'
      })
    } else {
      const center = geometry.getCenter()
      const radius = geometry.getRadius()
      const point = new Point([center[0], center[1]]).transform(projection, PROJECTION_TYPE).getFlatCoordinates()
      drawCallback({
        center: [point[0], point[1]],
        radius,
        type: 'circle'
      })
    }
  }, [drawCallback, degreesToRadians, getProjection])

  const createDrawingVector = useCallback(async (shape: 'circle' | 'polygon') => {
    const type = LAYER_TYPE.DRAWING
    await clearLayers(type)
    const source = new VectorSource({ wrapX: false })
    const layer = getLayer(type)
    if (!layer) {
      addMapLayer(source, type)
    }
    const drawInteraction = map.current?.getInteractions().getArray()
      .find((interaction) => interaction instanceof Draw)

    if (drawInteraction) map.current?.removeInteraction(drawInteraction)
    const newInteraction = new Draw({
      source,
      type: DRAWING_MAP[shape ?? 'circle']
    })

    newInteraction.on('drawend', (e) => {
      onDrawEnd(e, shape)
      map.current?.removeInteraction(newInteraction)
    })
    map.current?.addInteraction(newInteraction)
  }, [map, addMapLayer, clearLayers, getLayer, onDrawEnd])

  useEffect(() => {
    if (liveCams) {
      const type = LAYER_TYPE.LIVE_CAMS
      clearLayers(type)
      addToQueue(type)
      updateMapEvents()

      const zoom = map?.current?.getView().getZoom()
      const features = Object.values(liveCamLookup)?.reduce((arr: Feature[], cam) => {
        const point = createPoint(cam.lat, cam.lon, false)
        const pointFeature = new Feature(point)
        pointFeature.set('type', type)
        pointFeature.set('cam_id', cam.id)
        pointFeature.setStyle(new Style({
          image: new olIcon(getPointStyle(zoom, ICON.VIDEO_MAP, false))
        }))
        arr.push(pointFeature)
        return arr
      }, [])

      const source = new VectorSource({
        features
      })

      addMapLayer(source, type, viewOptions?.liveCams ?? false, { zIndex: 1 })
      removeFromQueue(type)
    }
  }, [liveCams])

  useEffect(() => {
    if (weather) {
      const type = LAYER_TYPE.WEATHER
      clearLayers(type)
      addToQueue(type)
      updateMapEvents()

      const zoom = map?.current?.getView().getZoom()
      const features = weather?.reduce((arr: Feature[], w) => {
        const point = createPoint(w.Breidd, w.Lengd, false)
        const pointFeature = new Feature(point)
        pointFeature.set('type', type)
        pointFeature.set('weather_id', w.Nr)
        pointFeature.setStyle(new Style({
          image: new olIcon(getPointStyle(zoom, ICON.WEATHER_MAP, false))
        }))
        arr.push(pointFeature)
        return arr
      }, [])

      const source = new VectorSource({
        features
      })

      addMapLayer(source, type, viewOptions?.weather ?? false, { zIndex: 1 })
      removeFromQueue(type)
    }
  }, [weather])

  useEffect(() => {
    if (roadConditions && map.current) {
      const type = LAYER_TYPE.ROAD_CONDITIONS
      clearLayers(type)
      addToQueue(type)
      updateMapEvents()

      const { roads_to_draw } = roadConditions
      const features = roads_to_draw?.reduce((arr: Feature[], road) => {
        const { road_coordinates } = road
        const coords = road_coordinates.reduce((arr: Coordinate[], coord) => {
          const point = createPoint(coord[1], coord[0], false)
          arr.push(point.getCoordinates())
          return arr
        }, [])

        const feature = new Feature({
          geometry: new MultiLineString([coords]),
          type,
          road_id: road.road_internal_id
        })
        feature.setId(road.road_internal_id)
        feature.setStyle([
          new Style({ // Border
            stroke: new Stroke({
              color: road.road_colors[1],
              width: 5
            })
          }),
          new Style({ // Inner color
            stroke: new Stroke({
              color: road.road_colors[0],
              width: 3
            })
          })
        ])
        arr.push(feature)

        if (road.point_id) { // Add road warnings
          const point = createPoint(road.point_lat, road.point_lon, false)
          const zoom = map?.current?.getView().getZoom()
          const pointFeature = new Feature(point)
          pointFeature.set('type', LAYER_TYPE.ROAD_SIGNS)
          pointFeature.set('road_id', road.road_internal_id)
          pointFeature.setStyle(new Style({
            image: new olIcon(getPointStyle(zoom, road.point_image, true))
          }))
          arr.push(pointFeature)
        }

        return arr
      }, [])

      const source = new VectorSource({
        features
      })

      addMapLayer(source, type, viewOptions?.roadConditions ?? false)
      removeFromQueue(type)
    }
  }, [roadConditions])

  useEffect(() => {
    if (points) {
      if (showTrails) {
        addToQueue(LAYER_TYPE.TRAILS)
        createTrailVector()
      }
      createMarkerVector()
    }
  }, [points])

  useEffect(() => {
    if (icons) {
      createIconVector()
    }
  }, [icons])

  useEffect(() => {
    if (polygons) {
      createPolygonVector()
    }
  }, [polygons])

  useEffect(() => {
    if (circles) {
      createCircleVector()
    }
  }, [circles])

  const enableDrawing = useCallback((isEnabled: boolean, shape?: 'circle' | 'polygon') => {
    if (isEnabled && shape) {
      setDrawingEnabled(true)
      createDrawingVector(shape)
    } else {
      setDrawingEnabled(false)
      clearLayers(LAYER_TYPE.DRAWING)
    }
  }, [createCircleVector, clearLayers])

  const mapZoom = useCallback((zoomFactor = 1) => {
    const view = map?.current?.getView()
    const zoom = view?.getZoom() ?? 0
    view?.setZoom(zoom + zoomFactor)
  }, [map])

  const classes = useMemo(() => {
    const classArr = ['FlotiMap']
    if (readonly) {
      classArr.push('readonly')
    }
    return classArr.join(' ')
  }, [readonly])

  const setMap = useCallback(async () => {
    if (mapRef.current && !map.current) {
      const center = mapSettings?.center ?? CENTER_ICELAND_COORDS
      const zoom = mapSettings?.zoom ?? DEFAULT_MAP_ZOOM

      map.current = new Map({
        target: mapRef.current,
        layers: tiles,
        view: new View({
          center,
          zoom
        }),
        overlays: []
      })

      if (readonly) {
        map.current?.getInteractions().forEach((i) => {
          i.setActive(false)
        })
      }

      if (defaultTile) {
        dispatch(setMapTile(defaultTile))
      }

      updateMapEvents()
    }
  }, [mapSettings, user, readonly, map, mapRef, defaultTile, updateMapEvents])

  useEffect(() => {
    setMap()
  }, [])

  useEffect(() => {
    // Has to be set after the map has been set for projection
    if (map && !mapSettings && user && user?.preferences?.MAP_LATITUDE_ON_LOAD) {
      const zoom = user?.preferences?.MAP_ZOOM_LEVEL_ON_LOAD
      const center = createPoint(user?.preferences?.MAP_LATITUDE_ON_LOAD, user?.preferences?.MAP_LONGITUDE_ON_LOAD)
      setMapSettings({
        zoom,
        center: center.getFlatCoordinates()
      })
    }
  }, [map])

  const forwardRefAction = useDynamicRef({
    setPoints,
    setIcons,
    setPolyshapes,
    setCircles,
    getMapSettings,
    setMapSettings,
    getProjection,
    enableDrawing,
    fitInMap,
    makeBlink,
    isBusy,
    setInfoDisplay
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  useEffect(() => {
    const layers = map?.current?.getLayers()?.getArray()
    layers?.forEach((layer) => {
      const type = layer.get('type')
      const key = layer.get('key')
      if (type === 'tile') {
        if (key === selectedTile) layer.setVisible(true)
        else layer.setVisible(false)
      }
    })
  }, [selectedTile])

  useEffect(() => {
    const layers = map?.current?.getLayers()?.getArray()
    layers?.forEach((layer) => {
      const type = layer.get('type')
      if (type === LAYER_TYPE.CLUSTER) layer.setVisible(!!viewOptions?.clusters)
      else if (type === LAYER_TYPE.MARKERS) layer.setVisible(!viewOptions?.clusters)
    })
  }, [viewOptions?.clusters])

  const mapLogo = useMemo(() => {
    const { logo } = MAP_TYPES[selectedTile]
    if (!logo) return null

    return (
      <div className="FlotiMap__logo">
        <img src={google_logo} />
      </div>
    )
  }, [selectedTile])

  return (
    <div className={classes}>
      <div id="map" className="FlotiMap__map" ref={mapRef}>
        {!readonly && options.showZoom && (
          <div className="FlotiMap__zoom">
            <div className="FlotiMap__zoom-in">
              <Button
                type='tertiary'
                size='s'
                label={<Icon icon={ICON.ADD} color="#393939" />}
                onClick={() => mapZoom(1)}
              />
            </div>
            <div className="FlotiMap__zoom-out">
              <Button
                type='tertiary'
                size='s'
                label={<Icon icon={ICON.SUBTRACT} color="#393939" />}
                onClick={() => mapZoom(-1)}
              />
            </div>
          </div>
        )}
        {infoDisplay && (
          <div className="FlotiMap__InfoDisplay">
            {infoDisplay.label && (
              <div className="FlotiMap__InfoDisplay-label">
                {infoDisplay.label}
              </div>
            )}
            <div className="FlotiMap__InfoDisplay-content">
              {infoDisplay.content}
            </div>
          </div>
        )}
        {!readonly && (
          <div className="FlotiMap__settings">
            {options.showTiles && mapTypeSelector}
            {options.showOptions && viewOptionsSelector}
          </div>
        )}
        {mapLogo}
      </div>
      <div>
        <div id="overlay" style={{ display: 'none' }}>
          <div id="overlay_text" />
          <div
            className="FlotiMap__overlay-close"
            onClick={() => removeElem('overlay')}
          >
            <Icon icon={ICON.CLOSE} size={16} />
          </div>
        </div>
      </div>

      {drawingEnabled && (
        <Infobox
          closable={true}
          status="info"
          type="inline"
          label={t('Info')}
          message={t('start drawing on the map')}
        />
      )}
    </div>
  )
}))

export default forwardRef((props, ref) => (
  <FlotiMapState>
    <FlotiMap ref={ref} {...props} />
  </FlotiMapState>
))
